import { useEffect, useState } from "react";
import { collection, getDocs, query, where } from "firebase/firestore";
import { db } from "./firebase";
import { ClubEvent, Game, Participant, Season } from "./types";
import { computeStandings, LeagueStandings } from "./utils/league";

/**
 * Kauden taulukko: hakee kauden tapahtumat sekä päättyneiden iltojen
 * osallistujat ja ottelut, ja laskee taulukon selaimessa. Tulevien
 * iltojen rivejä ei haeta, koska niistä ei kerry pisteitä.
 *
 * Ottelut haetaan, koska kausi lasketaan tehdyistä pisteistä (2.11.2026).
 * Aiemmin riitti osallistujarivin `finalPosition`, mutta sijoitus ei
 * kerro montako pistettä illassa tehtiin. Samalla taulukko ei enää nojaa
 * kantaan kirjattuun sijoitukseen: jos illan tulosta korjataan, kausi
 * korjaantuu itsestään ilman illan uudelleenlaskentaa.
 */
export function useLeagueStandings(clubId: string, season: Season | null): {
  standings: LeagueStandings | null;
  loading: boolean;
  /** Profiilikuvat playerId:n mukaan. Vain niille joilla kuva on. */
  photos: Record<string, string>;
} {
  const [standings, setStandings] = useState<LeagueStandings | null>(null);
  const [loading, setLoading] = useState(true);
  const [photos, setPhotos] = useState<Record<string, string>>({});

  useEffect(() => {
    if (!season) { setStandings(null); setPhotos({}); setLoading(false); return; }
    let alive = true;
    setLoading(true);
    (async () => {
      try {
        const evSnap = await getDocs(
          query(collection(db, "clubs", clubId, "events"), where("seasonId", "==", season.id))
        );
        const events = evSnap.docs.map(d => ({ id: d.id, ...d.data() } as ClubEvent));
        const finished = events.filter(e => e.status === "finished");
        const haetut = await Promise.all(finished.map(async ev => {
          const [pSnap, gSnap] = await Promise.all([
            getDocs(collection(db, "clubs", clubId, "events", ev.id, "participants")),
            getDocs(collection(db, "clubs", clubId, "events", ev.id, "games")),
          ]);
          return {
            id: ev.id,
            parts: pSnap.docs.map(d => ({ id: d.id, ...d.data() } as Participant)),
            games: gSnap.docs.map(d => ({ id: d.id, ...d.data() } as Game)),
          };
        }));
        const parts = haetut.map(h => [h.id, h.parts] as const);
        const games = haetut.map(h => [h.id, h.games] as const);
        // Profiilikuva on pelaajadokumentissa, ei osallistujarivissa, joten
        // se haetaan erikseen. Ilman tata liigan koroke nayttaa nimen
        // ensimmaisen kirjaimen vaikka pelaajalla on kuva kaikkialla
        // muualla.
        const plSnap = await getDocs(collection(db, "clubs", clubId, "players"));
        const photoMap: Record<string, string> = {};
        plSnap.docs.forEach(d => {
          const url = (d.data() || {}).photoUrl;
          if (typeof url === "string" && url) photoMap[d.id] = url;
        });
        if (!alive) return;
        setPhotos(photoMap);
        setStandings(computeStandings(
          season, events, Object.fromEntries(parts), Object.fromEntries(games)));
      } catch (err) {
        console.error("league standings", err);
        if (alive) { setStandings(null); setPhotos({}); }
      } finally {
        if (alive) setLoading(false);
      }
    })();
    return () => { alive = false; };
  }, [clubId, season]);

  return { standings, loading, photos };
}
